import { ref } from 'vue'
import apiInstance from '@/utils/api'

export interface OptionItem {
  id: string
  name: string
  price?: number
  usePrice?: boolean
  isSelected?: boolean
  image?: string
  mediaId?: number
  foodItemId?: number
  originalMediaId?: number
  originalMediaUrl?: string
}

export interface OptionGroup {
  id: string
  name: string
  items: OptionItem[]
  isRequired: boolean
  maxSelectableOptions?: number
  allowMultipleSelection?: boolean
  mediaId?: number
  image?: string
  // Matrix constrain ID (e.g. "1000007.2") — several groups can share the same
  // generic name, so this lets the UI tell them apart when the restaurant is matrix-synced.
  optionItemId?: string
}

export interface LocalizedContentEntry {
  name: string
}

export interface LocalizedContent {
  [language: string]: LocalizedContentEntry
}

export interface OptionSetItem {
  id: number
  food_item_id: number
  selected: number
  sort_position: number
  price: string
  use_price?: boolean | number
  media?: {
    id: number
    url: string
  }
  localizedContent: LocalizedContent
}

export interface FoodItemOptionGroup {
  id: number
  option_item_id?: string
  mandatory_choice: number
  maximum_choices: number
  allow_multiselect: number
  sort_position: number
  localizedContent: LocalizedContent
  option_set_items: OptionSetItem[]
}

export interface FoodItemOptionsResponse {
  error: boolean
  message: string
  data: FoodItemOptionGroup[]
}

export const useFoodItemOptions = () => {
  const optionGroups = ref<OptionGroup[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const errors = ref<string | null>(null)

  /**
   * Get the name from localizedContent based on preferred language
   * Falls back to first available language if preferred is not available
   */
  const getLocalizedName = (
    localizedContent: LocalizedContent,
    preferredLanguage: string = 'en'
  ): string => {
    if (!localizedContent) return 'Unnamed'

    // Try preferred language first
    if (localizedContent[preferredLanguage]?.name) {
      return localizedContent[preferredLanguage].name
    }

    // Fall back to German
    if (localizedContent.de?.name) {
      return localizedContent.de.name
    }

    // Fall back to English
    if (localizedContent.en?.name) {
      return localizedContent.en.name
    }

    // Return first available language
    for (const [_, content] of Object.entries(localizedContent)) {
      if (content.name) {
        return content.name
      }
    }

    return 'Unnamed'
  }

  /**
   * Transform API response to OptionGroup format
   */
  const transformApiResponse = (
    apiData: FoodItemOptionGroup[],
    preferredLanguage: string = 'en'
  ): OptionGroup[] => {
    // Sort by sort_position
    const sortedData = [...apiData].sort((a, b) => (a.sort_position || 0) - (b.sort_position || 0))

    return sortedData.map((group) => {
      // Transform option items
      const items: OptionItem[] = group.option_set_items
        .sort((a, b) => (a.sort_position || 0) - (b.sort_position || 0))
        .map((item) => ({
          id: `item-${item.id}`,
          name: getLocalizedName(item.localizedContent, preferredLanguage),
          price: parseFloat(item.price || '0'),
          usePrice: Boolean(Number(item.use_price)),
          isSelected: Number(item.selected) === 1,
          foodItemId: item.food_item_id,
          originalMediaId: item.media?.id,
          originalMediaUrl: item.media?.url,
          image: item.media?.url,
          mediaId: item.media?.id,
        }))

      return {
        id: `group-${group.id}`,
        name: getLocalizedName(group.localizedContent, preferredLanguage),
        items,
        isRequired: group.mandatory_choice === 1,
        maxSelectableOptions: group.maximum_choices > 0 ? group.maximum_choices : undefined,
        allowMultipleSelection: group.allow_multiselect === 1,
        optionItemId: group.option_item_id || undefined,
      }
    })
  }

  /**
   * Fetch food item options from the backend API
   * If foodItemId is provided, fetches options for that specific item (edit mode)
   * If foodItemId is not provided, fetches default/template options (add new mode)
   */
  const fetchFoodItemOptions = async (
    restaurantId: string,
    foodItemId?: number | string,
    preferredLanguage: string = 'en'
  ): Promise<OptionGroup[]> => {
    try {
      isLoading.value = true
      error.value = null

      const endpoint = foodItemId
        ? `/restaurant/${restaurantId}/food-item-option?food_item_id=${foodItemId}`
        : `/restaurant/${restaurantId}/food-item-option`

      const response = await apiInstance.get<FoodItemOptionsResponse>(endpoint)

      const data = response.data

      if (data.error === false && Array.isArray(data.data)) {
        const transformed = transformApiResponse(data.data, preferredLanguage)
        optionGroups.value = transformed
        return transformed
      } else {
        const errorMessage = data.message || 'Failed to fetch food item options'
        console.error('❌ Error fetching food item options:', errorMessage)
        error.value = errorMessage
        return []
      }
    } catch (e: any) {
      const errorMessage =
        e.response?.data?.message ||
        e.message ||
        'Network error while fetching food item options'
      console.error('❌ Error fetching food item options:', errorMessage)
      error.value = errorMessage
      return []
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Upload media for an option group
   */
  const uploadMedia = async (
    restaurantId: string,
    file: File,
    optionGroupId?: string
  ): Promise<{ id: number; original_url: string } | null> => {
    try {
      if (!restaurantId) {
        console.error('🔴 Restaurant ID is required for media upload')
        return null
      }

      if (!file) {
        console.error('🔴 File is required for media upload')
        return null
      }

      const formData = new FormData()
      formData.append('type', 'food_item')
      if (optionGroupId) {
        formData.append('type_id', optionGroupId)
      }
      formData.append('media[]', file)

      const response = await apiInstance.post(`/restaurant/${restaurantId}/upload-media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      const data = response.data

      if (data.error === false && data.data) {
        const mediaArray = Array.isArray(data.data) ? data.data : data.data.media_ids

        if (Array.isArray(mediaArray) && mediaArray.length > 0) {
          const mediaItem = mediaArray[0]
          const result = {
            id: mediaItem.id,
            original_url: mediaItem.original_url,
          }
          return result
        }
      }

      console.warn('⚠️ Upload response indicates failure or missing data:', data)
      return null
    } catch (err) {
      console.error('🔴 Error uploading option group media:', err)
      throw err
    }
  }

  /**
   * Delete media for an option group
   */
  const deleteMedia = async (
    restaurantId: string,
    mediaId: number,
    optionGroupId?: string
  ): Promise<boolean> => {
    try {
      if (!restaurantId) {
        console.error('🔴 Restaurant ID is required for media deletion')
        return false
      }

      if (!mediaId) {
        console.error('🔴 Media ID is required for media deletion')
        return false
      }

      const payload: any = {
        type: 'food_item',
      }

      if (optionGroupId) {
        payload.type_id = optionGroupId
      }

      const response = await apiInstance.delete(`/restaurant/${restaurantId}/delete-media/${mediaId}`, {
        data: payload,
      })

      const data = response.data

      if (data.error === false || response.status === 200 || response.status === 204) {
        return true
      }

      console.warn('⚠️ Deletion response indicates failure:', data)
      return false
    } catch (err) {
      console.error('🔴 Error deleting option group media:', err)
      throw err
    }
  }

  /**
   * Update an existing option group via API
   */
  const updateOptionGroup = async (
    restaurantId: string,
    optionGroupId: string | number,
    groupName: string,
    isRequired: boolean,
    maxSelectableOptions: number | null | undefined,
    allowMultipleSelection: boolean,
    items: OptionItem[],
    deletedOptionIds: (number | string)[],
    activeLanguage: string = 'en'
  ): Promise<boolean> => {
    try {
      if (!restaurantId) {
        console.error('🔴 Restaurant ID is required for updating option group')
        return false
      }

      if (!optionGroupId) {
        console.error('🔴 Option Group ID is required for updating')
        return false
      }

      if (!groupName.trim()) {
        console.error('🔴 Group name is required')
        return false
      }

      if (items.length === 0) {
        console.error('🔴 At least one option is required')
        return false
      }

      // Extract the numeric ID from the group ID if it's in format "group-123"
      const groupIdValue = typeof optionGroupId === 'string'
        ? optionGroupId.replace('group-', '')
        : optionGroupId

      // Build the request payload
      const payload: any = {
        mandatory_choice: isRequired ? 1 : 0,
        maximum_choices: maxSelectableOptions || 1,
        allow_multiselect: allowMultipleSelection ? 1 : 0,
        localizedContent: {
          [activeLanguage]: {
            name: groupName.trim(),
          },
        },
        options: items.map(item => {
          const option: any = {
            price: item.price || 0,
            use_price: item.usePrice || false,
            food_item_id: item.foodItemId || 0,
            localizedContent: {
              [activeLanguage]: {
                name: item.name,
              },
            },
          }

          // If the item has an originalMediaId or mediaId, use it
          // Extract numeric ID from "item-123" format if needed
          if (item.originalMediaId) {
            option.media_id = item.originalMediaId
          } else if (item.mediaId) {
            option.media_id = item.mediaId
          }

          // If the item has an ID from the API (not a temporary ID), include it
          const itemId = item.id.replace('item-', '')
          if (!isNaN(Number(itemId)) && itemId !== item.id) {
            option.id = Number(itemId)
          } else if (!item.id.startsWith('item-')) {
            // If it's already a number-like ID, use it
            option.id = Number(item.id)
          }

          return option
        }),
      }

      // Only include deleted_option_ids if there are any
      if (deletedOptionIds.length > 0) {
        payload.deleted_option_ids = deletedOptionIds.map(id => {
          // Extract numeric ID from "item-123" format if needed
          const numId = typeof id === 'string' ? Number(id.replace('item-', '')) : Number(id)
          return isNaN(numId) ? id : numId
        })
      }

      const response = await apiInstance.put(
        `/restaurant/${restaurantId}/food-item-option/${groupIdValue}`,
        payload
      )

      if (response.status === 200 && response.data.error === false) {
        return true
      } else {
        const errorMessage =
          response.data.message || 'Failed to update option group'
        console.error('❌ Error updating option group:', errorMessage)
        error.value = errorMessage
        return false
      }
    } catch (err: any) {
      errors.value = err.response?.data?.errors ?? null
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Network error while updating option group'
      console.error('🔴 Error updating option group:', errorMessage)
      error.value = errorMessage
      return false
    }
  }

  /**
   * Create a new option group via API
   * Payload format:
   * {
   *   "mandatory_choice": 1 | 0,
   *   "maximum_choices": number,
   *   "allow_multiselect": 1 | 0,
   *   "localizedContent": {
   *     "en": { "name": "..." },
   *     "de": { "name": "..." }
   *   },
   *   "options": [
   *     {
   *       "price": number,
   *       "media_id": number,
   *       "localizedContent": { ... }
   *     }
   *   ]
   * }
   */
  const createOptionGroup = async (
    restaurantId: string,
    groupName: string,
    isRequired: boolean,
    maxSelectableOptions: number | null | undefined,
    allowMultipleSelection: boolean,
    items: OptionItem[],
    activeLanguage: string = 'en'
  ): Promise<boolean> => {
    try {
      if (!restaurantId) {
        console.error('🔴 Restaurant ID is required for creating option group')
        return false
      }

      if (!groupName.trim()) {
        console.error('🔴 Group name is required')
        return false
      }

      if (items.length === 0) {
        console.error('🔴 At least one option is required')
        return false
      }

      // Build the request payload
      const payload = {
        mandatory_choice: isRequired ? 1 : 0,
        maximum_choices: maxSelectableOptions || 1,
        allow_multiselect: allowMultipleSelection ? 1 : 0,
        localizedContent: {
          [activeLanguage]: {
            name: groupName.trim(),
          },
        },
        options: items.map(item => ({
          price: item.price || 0,
          use_price: item.usePrice || false,
          media_id: item.mediaId || null,
          food_item_id: item.foodItemId || 0,
          localizedContent: {
            [activeLanguage]: {
              name: item.name,
            },
          },
        })),
      }

      const response = await apiInstance.post(
        `/restaurant/${restaurantId}/food-item-option`,
        payload
      )

      if (response.status === 200 && response.data.error === false) {
        return true
      } else {
        const errorMessage =
          response.data.message || 'Failed to create option group'
        console.error('❌ Error creating option group:', errorMessage)
        error.value = errorMessage
        return false
      }
    } catch (err: any) {
      errors.value = err.response?.data?.errors ?? null
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Network error while creating option group'
      console.error('🔴 Error creating option group:', errorMessage)
      error.value = errorMessage
      return false
    }
  }

  /**
   * Sort option groups by sending sort positions to the backend
   */
  const sortOptionGroups = async (
    restaurantId: string,
    groupsToSort: Array<{ id: string | number; sortPosition: number }>
  ): Promise<boolean> => {
    try {
      if (!restaurantId) {
        console.error('🔴 Restaurant ID is required for sorting option groups')
        return false
      }

      if (!groupsToSort || groupsToSort.length === 0) {
        console.warn('⚠️ No groups to sort')
        return true
      }

      // Transform the frontend group IDs to API format
      const items = groupsToSort.map(group => {
        const groupId = typeof group.id === 'string'
          ? Number(group.id.replace('group-', ''))
          : Number(group.id)

        return {
          id: groupId,
          sort_position: group.sortPosition,
        }
      })

      const response = await apiInstance.post(
        `/restaurant/${restaurantId}/food-item-option/sort-items`,
        { items }
      )

      if (response.status === 200 && response.data.error === false) {
        return true
      } else {
        const errorMessage =
          response.data.message || 'Failed to sort option groups'
        console.error('❌ Error sorting option groups:', errorMessage)
        error.value = errorMessage
        return false
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Network error while sorting option groups'
      console.error('🔴 Error sorting option groups:', errorMessage)
      error.value = errorMessage
      return false
    }
  }

  const reset = () => {
    optionGroups.value = []
    isLoading.value = false
    error.value = null
  }

  return {
    optionGroups,
    isLoading,
    error,
    errors,
    fetchFoodItemOptions,
    uploadMedia,
    deleteMedia,
    createOptionGroup,
    updateOptionGroup,
    sortOptionGroups,
    reset,
    transformApiResponse,
  }
}
